import React from 'react';
import { Trash2 } from 'lucide-react';
import { api, BookingMode, MarkBookingPeriod } from '../api.ts';
import { TYPE, useTheme } from '../theme';
import { Button, Card, CardHeader, Field, Icon, IconButton, Input, Segmented } from '../components/ui';

/**
 * Kalenderns bokningsläge (API: lib/bookingRules.ts).
 * Öppen: allt går att boka utom blockerade perioder. Stängd: inget går att boka utom bokningsbara perioder.
 * Båda typerna sparas; bara de som hör till läget visas och gäller.
 */
interface Props {
  markId: string;
  canEdit: boolean;
}

const MODES: { k: BookingMode; l: string; d: string }[] = [
  { k: 'open', l: 'Öppen för bokning', d: 'Allt går att boka utom perioder du blockerar.' },
  { k: 'closed', l: 'Stängd för bokning', d: 'Inget går att boka utom perioder du anger som bokningsbara.' },
];

const fmt = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });

export function BookingModePanel({ markId, canEdit }: Props) {
  const T = useTheme();
  const [mode, setMode] = React.useState<BookingMode | null>(null);
  const [periods, setPeriods] = React.useState<MarkBookingPeriod[]>([]);
  const [form, setForm] = React.useState({ start: '', end: '', note: '' });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    let live = true;
    api.markBookingCalendar(markId)
      .then(c => { if (live && c) { setMode(c.bookingMode); setPeriods(c.bookingPeriods); } })
      .catch(e => live && setErr((e as Error).message));
    return () => { live = false; };
  }, [markId]);

  const errLine = err && <div role="alert" style={{ color: T.danger, fontSize: 13, fontWeight: 600, marginTop: 10 }}>{err}</div>;
  if (!mode) return err ? <div style={{ marginBottom: 16 }}>{errLine}</div> : null;

  const kind = mode === 'closed' ? 'bookable' : 'blocked';
  const shown = periods.filter(p => p.kind === kind);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  const changeMode = (m: BookingMode) => m !== mode && run(async () => setMode(await api.setMarkBookingMode(markId, m)));
  const add = () => run(async () => {
    if (!form.start) throw new Error('Välj ett startdatum');
    const p = await api.addMarkBookingPeriod(markId, { kind, start: form.start, end: form.end || form.start, note: form.note || null });
    setPeriods(prev => [...prev, p].sort((a, b) => a.start.localeCompare(b.start)));
    setForm({ start: '', end: '', note: '' });
  });
  const remove = (id: string) => run(async () => {
    await api.removeMarkBookingPeriod(id);
    setPeriods(prev => prev.filter(p => p.id !== id));
  });

  const current = MODES.find(m => m.k === mode)!;

  return (
    <Card style={{ marginBottom: 24 }} data-testid="booking-mode-panel">
      <CardHeader title="Bokning" sub={current.d} />
      {canEdit
        ? <Segmented label="Bokningsläge" value={mode} onChange={changeMode} options={MODES.map(m => ({ value: m.k, label: m.l, disabled: busy }))} style={{ marginBottom: 16 }} />
        : <div style={{ color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>{current.l}</div>}

      <div style={{ ...TYPE.label, color: T.textMuted, marginBottom: 4 }}>
        {kind === 'blocked' ? 'Blockerade perioder' : 'Bokningsbara perioder'} ({shown.length})
      </div>
      <div style={{ marginBottom: canEdit ? 16 : 0 }}>
        {shown.map((p, i) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 48, borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ color: T.text, fontSize: 15, fontWeight: 600 }}>{p.start === p.end ? fmt(p.start) : `${fmt(p.start)} – ${fmt(p.end)}`}</span>
              {p.note && <span style={{ ...TYPE.small, color: T.textMuted, marginLeft: 8 }}>{p.note}</span>}
            </div>
            {canEdit && <IconButton label="Ta bort period" disabled={busy} onClick={() => remove(p.id)} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>}
          </div>
        ))}
        {!shown.length && <div style={{ ...TYPE.small, color: T.textMuted, padding: '8px 0' }}>
          {kind === 'blocked' ? 'Inga blockeringar – alla datum går att boka.' : 'Inga bokningsbara perioder – inget går att boka.'}
        </div>}
      </div>

      {canEdit && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
          <Field label="Från" style={{ flex: '1 1 150px' }}><Input type="date" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value, end: f.end && f.end < e.target.value ? e.target.value : f.end }))} /></Field>
          <Field label="Till" style={{ flex: '1 1 150px' }}><Input type="date" value={form.end} min={form.start || undefined} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} /></Field>
          <Field label="Anteckning" style={{ flex: '2 1 180px' }}><Input value={form.note} placeholder="Valfri" onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Field>
          <Button variant="secondary" loading={busy} onClick={add}>
            {kind === 'blocked' ? 'Blockera period' : 'Lägg till bokningsbar period'}
          </Button>
        </div>
      )}
      {errLine}
    </Card>
  );
}
